import {
  NEW_YORK_TIMEZONE,
  usePriceMarketReactionData,
} from '@terminal/hooks/use-price-market-reaction-data/usePriceMarketReactionData';
import { LineData } from '@terminal/modules/market-reaction-charts/types';
import { useEntities } from '@terminal/react-query-hooks/entities/useEntities';
import { useTheme } from '@terminal/react-query-hooks/themes/useThemeOverview';
import { useThemePerformanceTimeseries } from '@terminal/react-query-hooks/themes/useThemePerformanceTimeseries';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { isSameDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import { MarketCatalystReactionChartData } from '../market-catalyst-reaction.types';
import { buildSingleEtfLine } from '../utils/buildSingleEtfLine';

type UseSingleEtfMarketReactionLinesArgs = {
  tags: string[];
  tickers: string[];
  createdAt: string;
  themeId?: string;
};

export const useSingleEtfMarketReactionLines = ({
  tags,
  tickers,
  createdAt,
  themeId,
}: UseSingleEtfMarketReactionLinesArgs): MarketCatalystReactionChartData => {
  const isIntradayChartEnabled = useFlags(state =>
    state.getFeatureFlagState('theme_intraday_chart')
  );
  const isThemeTimeseriesEnabled = !!themeId && isIntradayChartEnabled;

  const {
    priceData,
    fromDateEST,
    toDateEST,
    lastProportionChange,
    lastKnownPrice,
    prevCloseData,
    lastKnownDate,
    isDailyFallback,
    isLoading: isPriceMarketReactionDataLoading,
  } = usePriceMarketReactionData({
    entityTag: tags[0],
    ticker: tickers[0],
    reportingDate: createdAt,
    isMultipleEntitiesChart: false,
    variant: 'catalyst',
    isMarketCatalyst: true,
  });

  const { data: entitiesData } = useEntities({
    entityTags: tags,
  });

  const { data: themeTimeseries, isLoading: isThemeTimeseriesLoading } =
    useThemePerformanceTimeseries(
      { themeId: themeId ?? '', horizon: 'intraday' },
      isThemeTimeseriesEnabled
    );

  const { data: themeData } = useTheme({
    themeId: isThemeTimeseriesEnabled ? themeId : undefined,
  });

  const themeAggregate = themeTimeseries?.aggregate;
  const themeName = themeData?.name;

  // The ETF chart is anchored to the insight's createdAt day. Only render the
  // theme intraday line when its latest data point falls on that same ET day,
  // so the two lines stay on the same date
  const themeIntradayIso = themeAggregate?.at(-1)?.index;
  const isThemeIntradayOnSameDay =
    !!themeIntradayIso &&
    isSameDay(
      toZonedTime(themeIntradayIso, NEW_YORK_TIMEZONE),
      toZonedTime(createdAt, NEW_YORK_TIMEZONE)
    );

  const hasIntradayThemeTimeseries =
    !!themeAggregate?.length && !!themeName && isThemeIntradayOnSameDay;

  const etfEntity = entitiesData[0];

  if (!etfEntity) {
    return {
      lines: [],
      fromDateEST,
      toDateEST,
      isDailyFallback,
      isLoading:
        isPriceMarketReactionDataLoading ||
        (isThemeTimeseriesEnabled && isThemeTimeseriesLoading),
    };
  }

  const etfLine = buildSingleEtfLine({
    entity: etfEntity,
    priceData,
    prevCloseData,
    lastProportionChange,
    lastKnownPrice,
    lastKnownDate,
    asPercentChange: hasIntradayThemeTimeseries,
  });

  const lines: LineData[] =
    hasIntradayThemeTimeseries && themeData
      ? [
          {
            data: themeAggregate.map(({ index, value }) => ({
              time: index,
              value,
            })),
            label: themeName,
            lastProportionChange: themeAggregate.at(-1)?.value,
            subject: { type: 'theme', data: themeData },
          },
          etfLine,
        ]
      : [etfLine];

  return {
    lines,
    fromDateEST,
    toDateEST,
    isDailyFallback,
    isLoading:
      isPriceMarketReactionDataLoading ||
      (isThemeTimeseriesEnabled && isThemeTimeseriesLoading),
  };
};
